import { useEffect } from 'react';
import {
  isStandalonePWA,
  hasWebPushSupport,
  getPushPermissionState,
} from '../../services/platformService';
import { useAuthStore } from '../../store/useAuthStore';
import { openPushPermissionModal, wasPwaPromptShown } from '../../services/pushPermissionModalService';
import { logDebug } from '../../services/loggerService';

/**
 * Renderless component that pops the push value modal the first time an
 * authenticated user launches HomePlated as an installed PWA in standalone
 * mode — iOS or Android alike.
 *
 * Why this exists: at registration (in a browser tab) we nudge install, but
 * the notification ask can slip through the cracks — on iOS-web the
 * permission API doesn't exist in Safari at all, and on Android the user can
 * go install via the ⋮ menu without ever dismissing the tab's chained push
 * prompt. Either way, once they install and launch the PWA, this trigger
 * detects that we're now standalone + have permission API access + haven't
 * asked yet, and opens the value-pitch modal whose "Enable" button satisfies
 * the OS user-gesture requirement for requestPermission().
 *
 * One-shot: persists `hp_push_pwa_prompt_shown` on close (set by the modal
 * itself when opened with `fromFirstLaunch: true`). After that, users in
 * the `default` state can re-trigger from Profile → Settings; denied/
 * granted users never see this again.
 *
 * Waits for auth restore so we don't fire for unauthenticated users (the
 * modal is meaningful only post-signin).
 */
export const PushFirstLaunchTrigger: React.FC = () => {
  const authLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (!isStandalonePWA()) return;
    if (!hasWebPushSupport()) return;
    if (getPushPermissionState() !== 'default') return;

    if (wasPwaPromptShown()) return;

    logDebug('PushFirstLaunchTrigger: opening modal for PWA first launch');
    openPushPermissionModal({ fromFirstLaunch: true });
  }, [authLoading, isAuthenticated]);

  return null;
};
